import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import {
  ChatflowAnalyticsFilters,
  ChatflowAnalyticsSummary,
  ChatflowBotPerformance,
  ChatflowAgentPerformance,
  ChatflowAgentPerformanceResponse,
  ChatflowNodePerformance,
  ChatflowNodePerformanceResponse,
  ChatflowQueueAnalytics
} from '../models/chatflow-analytics.model';
import { finDelDiaIso, inicioDelDiaIso } from './chatflow-date-range.util';

@Injectable({ providedIn: 'root' })
export class ChatflowAnalyticsApiService {
  private readonly base = `${environment.apiUrl}api/chatflows`;
  /** Rango máximo permitido en días */
  static readonly MAX_RANGE_DAYS = 366;

  constructor(private http: HttpClient) {}

  /** Valida los filtros en frontend antes de enviar. Devuelve mensaje de error o null. */
  validateFilters(filters: ChatflowAnalyticsFilters): string | null {
    if (!filters.from || !filters.to) return 'Selecciona un rango de fechas.';
    const from = new Date(filters.from);
    const to = new Date(filters.to);
    if (from > to) return 'La fecha de inicio debe ser anterior a la fecha de fin.';
    const diffDays = Math.ceil((to.getTime() - from.getTime()) / 86_400_000);
    if (diffDays > ChatflowAnalyticsApiService.MAX_RANGE_DAYS) {
      return `El rango máximo es de ${ChatflowAnalyticsApiService.MAX_RANGE_DAYS} días.`;
    }
    return null;
  }

  /**
   * `from`/`to` llegan como día (`yyyy-MM-dd`) desde los `<input type="date">` y viajan como
   * instantes que cubren el día completo: el converter del backend resuelve una fecha suelta al
   * INICIO del día, así que `to` en crudo dejaba fuera todo lo ocurrido ese mismo día (inc51,
   * mismo defecto que en las métricas CSAT).
   */
  private buildParams(filters: ChatflowAnalyticsFilters): HttpParams {
    let p = new HttpParams().set('from', inicioDelDiaIso(filters.from)).set('to', finDelDiaIso(filters.to));
    if (filters.timezone) p = p.set('timezone', filters.timezone);
    if (filters.channel) p = p.set('channel', filters.channel);
    if (filters.teamId) p = p.set('teamId', filters.teamId);
    if (filters.agentId) p = p.set('agentId', filters.agentId);
    if (filters.languageCode) p = p.set('languageCode', filters.languageCode);
    return p;
  }

  getSummary(chatflowId: number, filters: ChatflowAnalyticsFilters): Observable<ChatflowAnalyticsSummary> {
    return this.http.get<ChatflowAnalyticsSummary>(
      `${this.base}/${chatflowId}/analytics/summary`,
      { params: this.buildParams(filters) }
    );
  }

  getBotPerformance(chatflowId: number, filters: ChatflowAnalyticsFilters): Observable<ChatflowBotPerformance> {
    return this.http.get<ChatflowBotPerformance>(
      `${this.base}/${chatflowId}/analytics/bot-performance`,
      { params: this.buildParams(filters) }
    );
  }

  /** El backend responde `{ chatflowId, agents }`: aquí se devuelve solo la lista. */
  getAgentPerformance(chatflowId: number, filters: ChatflowAnalyticsFilters): Observable<ChatflowAgentPerformance[]> {
    return this.http.get<ChatflowAgentPerformanceResponse>(
      `${this.base}/${chatflowId}/analytics/agent-performance`,
      { params: this.buildParams(filters) }
    ).pipe(map((respuesta) => respuesta?.agents ?? []));
  }

  /** El backend responde `{ chatflowId, nodes }`: aquí se devuelve solo la lista. */
  getNodePerformance(chatflowId: number, filters: ChatflowAnalyticsFilters): Observable<ChatflowNodePerformance[]> {
    return this.http.get<ChatflowNodePerformanceResponse>(
      `${this.base}/${chatflowId}/analytics/node-performance`,
      { params: this.buildParams(filters) }
    ).pipe(map((respuesta) => respuesta?.nodes ?? []));
  }

  getQueueAnalytics(chatflowId: number, filters: ChatflowAnalyticsFilters): Observable<ChatflowQueueAnalytics> {
    return this.http.get<ChatflowQueueAnalytics>(
      `${this.base}/${chatflowId}/analytics/queue`,
      { params: this.buildParams(filters) }
    );
  }
}
